// Vyklápěcí panel pod tlačítkem „Vygenerovat dopis" v detailu nemovitosti.
//
// Workflow:
//   1. Vyber vlastníky checkboxy (default: všichni vybráni).
//   2. Vyber šablonu.
//   3. „Vygenerovat" → POST /api/mailings paralelně pro každého vybraného →
//      pro každého PDF + status (ok/error).
//   4. PDFka se otevírají přes „Otevřít" / „Stáhnout" na řádku každého výstupu.
//
// Po každém úspěšném generování invalidujeme `['mailings', propertyId]`,
// ať se Historie dopisů níže sama refreshne.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '@/lib/api';
import type {
  GenerateLetterResponse,
  OwnerWithContacts,
  Template,
} from '@/lib/types';

type Props = {
  propertyId: string;
  owners: OwnerWithContacts[];
  onClose: () => void;
};

type GenResult = {
  ownerId: string;
  ownerName: string;
  status: 'ok' | 'error';
  mailingId?: string;
  pdfBytes?: number;
  error?: string;
};

export default function GenerateLetterPanel({ propertyId, owners, onClose }: Props) {
  const qc = useQueryClient();

  const eligibleOwners = useMemo(
    () => owners.filter((o) => o.contacts.some((c) => c.channel === 'postal' && !c.optOut)),
    [owners],
  );
  const ineligibleCount = owners.length - eligibleOwners.length;

  // Default: všichni eligible vybráni — v 99 % případů stejný dopis pro všechny.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(eligibleOwners.map((o) => o.id)),
  );
  const [templateId, setTemplateId] = useState<string>('');
  const [results, setResults] = useState<GenResult[]>([]);

  const templatesQuery = useQuery({
    queryKey: ['templates'],
    queryFn: () => api<{ ok: true; items: Template[] }>('/api/templates'),
  });
  const templates = templatesQuery.data?.items || [];

  if (templates.length > 0 && !templateId) {
    setTemplateId(templates[0].id);
  }

  const allSelected = selectedIds.size === eligibleOwners.length && eligibleOwners.length > 0;

  function toggleAll() {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(eligibleOwners.map((o) => o.id)));
  }

  function toggleOne(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const generate = useMutation({
    mutationFn: async (): Promise<GenResult[]> => {
      const ids = Array.from(selectedIds);
      const settled = await Promise.allSettled(
        ids.map((ownerId) =>
          api<GenerateLetterResponse>('/api/mailings', {
            method: 'POST',
            json: { propertyId, ownerId, templateId },
          }),
        ),
      );
      return ids.map((ownerId, i) => {
        const owner = eligibleOwners.find((o) => o.id === ownerId);
        const r = settled[i];
        if (r.status === 'fulfilled') {
          return {
            ownerId,
            ownerName: owner?.name || ownerId,
            status: 'ok',
            mailingId: r.value.mailingId,
            pdfBytes: r.value.pdfBytes,
          };
        }
        const e = r.reason;
        const msg =
          e instanceof ApiError
            ? `${e.message}${typeof e.body === 'object' && e.body && 'message' in e.body ? ' — ' + (e.body as { message?: string }).message : ''}`
            : e instanceof Error
              ? e.message
              : String(e);
        return { ownerId, ownerName: owner?.name || ownerId, status: 'error', error: msg };
      });
    },
    onSuccess: (res) => {
      setResults(res);
      qc.invalidateQueries({ queryKey: ['mailings', propertyId] });
    },
  });

  const okCount = results.filter((r) => r.status === 'ok').length;
  const errCount = results.length - okCount;

  return (
    <div className="rounded-lg border bg-muted/20">
      <div className="px-4 py-2.5 border-b bg-background flex items-center justify-between">
        <span className="text-sm font-medium">Vygenerovat dopis</span>
        <button
          onClick={onClose}
          className="text-xs text-muted-foreground hover:text-foreground"
          aria-label="Zavřít panel"
        >
          ✕ Zavřít
        </button>
      </div>

      <div className="p-4 space-y-4">
        {ineligibleCount > 0 && (
          <div className="text-xs text-muted-foreground">
            {ineligibleCount} z {owners.length} vlastníků nemá poštovní kontakt nebo je opt-out — v seznamu nejsou.
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Vlastníci (adresáti)
            </label>
            {eligibleOwners.length > 1 && (
              <button
                type="button"
                onClick={toggleAll}
                className="text-xs text-primary hover:underline"
              >
                {allSelected ? 'Zrušit výběr' : 'Vybrat všechny'}
              </button>
            )}
          </div>
          {eligibleOwners.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">
              Žádný vlastník s poštovním kontaktem.
            </div>
          ) : (
            <ul className="divide-y rounded-md border bg-background">
              {eligibleOwners.map((o) => {
                const postalCount = o.contacts.filter((c) => c.channel === 'postal' && !c.optOut).length;
                return (
                  <li key={o.id}>
                    <label className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(o.id)}
                        onChange={() => toggleOne(o.id)}
                        className="shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {o.name}
                          {o.share && (
                            <span className="ml-2 text-xs font-normal text-muted-foreground tabular-nums">
                              {o.share}
                            </span>
                          )}
                        </div>
                        {o.address && (
                          <div className="text-xs text-muted-foreground truncate">{o.address}</div>
                        )}
                      </div>
                      {postalCount > 1 && (
                        <span className="text-[10px] text-muted-foreground shrink-0">
                          {postalCount} adres
                        </span>
                      )}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Šablona
          </label>
          <select
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            className="w-full rounded-md border bg-background px-2.5 py-1.5 text-sm"
            disabled={templates.length === 0}
          >
            {templates.length === 0 && <option value="">Žádné šablony</option>}
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => generate.mutate()}
            disabled={selectedIds.size === 0 || !templateId || generate.isPending}
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
          >
            {generate.isPending
              ? `Generuji ${selectedIds.size} PDF…`
              : `Vygenerovat ${selectedIds.size > 0 ? selectedIds.size : ''} ${selectedIds.size === 1 ? 'dopis' : 'dopisů'}`}
          </button>
          {generate.error && (
            <span className="text-xs text-destructive">
              Chyba: {String(generate.error)}
            </span>
          )}
        </div>

        {results.length > 0 && (
          <div className="rounded-md border bg-background">
            <div className="px-3 py-2 border-b text-xs text-muted-foreground">
              Hotovo · <span className="text-foreground font-medium">{okCount} OK</span>
              {errCount > 0 && (
                <> · <span className="text-destructive font-medium">{errCount} chyba</span></>
              )}
            </div>
            <ul className="divide-y">
              {results.map((r) => (
                <li key={r.ownerId} className="px-3 py-2 flex items-center gap-3 text-sm">
                  <span
                    className={
                      'inline-block w-2 h-2 rounded-full shrink-0 ' +
                      (r.status === 'ok' ? 'bg-green-500' : 'bg-red-500')
                    }
                  />
                  <div className="flex-1 min-w-0 truncate font-medium">{r.ownerName}</div>
                  {r.status === 'ok' && r.mailingId && (
                    <>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {Math.round((r.pdfBytes || 0) / 1024)} kB
                      </span>
                      <a
                        href={`/api/mailings/${r.mailingId}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="px-2 py-1 rounded border text-xs hover:bg-muted"
                      >
                        Otevřít
                      </a>
                      <a
                        href={`/api/mailings/${r.mailingId}/pdf`}
                        download
                        className="px-2 py-1 rounded border text-xs hover:bg-muted"
                      >
                        Stáhnout
                      </a>
                    </>
                  )}
                  {r.status === 'error' && (
                    <span className="text-xs text-destructive max-w-[60%] truncate" title={r.error}>
                      {r.error}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
